import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Stack,
  Typography,
  LinearProgress,
  Tooltip,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper';

const formatKWh = (value) =>
  Number.isFinite(value)
    ? new Intl.NumberFormat('da-DK', { maximumFractionDigits: 1 }).format(
        value,
      ) + ' kWh'
    : '—';

const formatPct = (value) =>
  Number.isFinite(value)
    ? new Intl.NumberFormat('da-DK', { maximumFractionDigits: 1 }).format(
        value,
      ) + '%'
    : '—';

const clampPct = (v) =>
  Number.isFinite(v) ? Math.min(100, Math.max(0, v)) : 0;

const EnergyTreeNodeDetailSummary = ({ clickedNode }) => {
  if (!clickedNode) return null;

  const {
    Name,
    TotalEnergyUsage,
    AnalysedEnergyUsagePercentage,
    AnalysedEnergyUsage,
    AverageEnergyUsage,
  } = clickedNode?.energySummary || {};

  const analysed = clampPct(AnalysedEnergyUsagePercentage);
  const typeColor = getTypeColor(clickedNode?.type) || '#1976d2'; // fallback

  return (
    <Card
      variant="outlined"
      sx={{ borderRadius: 2, p: 0.5, backgroundColor: alpha(typeColor, 0.05) }}
    >
      <CardHeader
        sx={{ p: 1, '& .MuiCardHeader-content': { overflow: 'hidden' } }}
        avatar={
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: typeColor,
              color: 'white',
              fontSize: 16,
            }}
          >
            <i className={`fa-solid ${getTypeIcon(clickedNode?.type)}`} />
          </Avatar>
        }
        title={
          <Typography variant="subtitle1" noWrap>
            {Name || 'Energy Node'}
          </Typography>
        }
        subheader={
          <Typography variant="caption" color="text.secondary" noWrap>
            {clickedNode?.type}
          </Typography>
        }
      />
      <CardContent sx={{ pt: 0.5, pb: 1, px: 1.25 }}>
        <Stack spacing={1}>
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
          >
            <Typography variant="caption">Forbrug</Typography>
            <Typography variant="body1" fontWeight={600}>
              {formatKWh(TotalEnergyUsage)}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
          >
            <Typography variant="caption">Gns. forbrug pr. time</Typography>
            <Typography variant="body1" fontWeight={600}>
              {formatKWh(AverageEnergyUsage)}
            </Typography>
          </Stack>

          {analysed !== null && AnalysedEnergyUsagePercentage !== null && (
            <Stack spacing={0.5}>
              <Divider />
              <Stack
                direction="row"
                alignItems="baseline"
                justifyContent="space-between"
              >
                <Typography variant="caption">Målt forbrug</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatKWh(AnalysedEnergyUsage)}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="caption"></Typography>
                <Typography variant="caption" fontWeight={600}>
                  {formatPct(analysed)}
                </Typography>
              </Stack>

              <Tooltip title={`${formatPct(analysed)} analysed`}>
                <LinearProgress
                  variant="determinate"
                  value={analysed}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    bgcolor: alpha(typeColor, 0.18),
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: typeColor,
                      transition: 'width .3s ease',
                    },
                  }}
                />
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

EnergyTreeNodeDetailSummary.propTypes = {
  clickedNode: PropTypes.shape({
    type: PropTypes.string,
    energySummary: PropTypes.shape({
      Name: PropTypes.string,
      TotalEnergyUsage: PropTypes.number,
      AnalysedEnergyUsagePercentage: PropTypes.number,
    }),
  }),
};

export default EnergyTreeNodeDetailSummary;
